import { useCallback, useEffect, useState } from 'react';
import { Check, Loader2, MessageSquarePlus, ThumbsUp, Trash2 } from 'lucide-react';
import {
  ROUTING_FEEDBACK_KINDS,
  routingFeedbackKind,
  routingFeedbackStatus,
  type RoutingFeedbackKind,
} from '@repo/shared-types';
import { api, type KbRun, type RoutingFeedback, type RoutingFeedbackInput } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useProjectTree } from '../../lib/project-tree';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, Textarea } from '../ui/input';
import { cn, timeAgo } from '../../lib/utils';

/** Everything the form needs to ask about a run, gathered once. */
interface RunShape {
  /** Projects this run reached — the only ones "went to the wrong project" can name. */
  destinations: Array<{ projectId: string; label: string }>;
  /** The arrivals it absorbed, so a report can name which one it is about. */
  parts: Array<{ index: number; title: string | null }>;
}

function shapeOf(run: KbRun): RunShape {
  return {
    // A pinned run has no `destinations`; the project it was given is the only one it could touch,
    // and that is already the run's own project — so there is nothing to choose between.
    destinations: (run.destinations ?? []).map((destination) => ({
      projectId: destination.projectId,
      label: destination.projectName || destination.projectPath,
    })),
    // One entry per arrival the run was handed, taken from whichever destination saw the most of
    // them — the numbering is the run's, so any destination agrees about what item 2 is.
    parts: [
      ...new Map(
        (run.destinations ?? [])
          .flatMap((destination) => destination.parts)
          .map((part) => [part.itemIndex, { index: part.itemIndex, title: part.title }]),
      ).values(),
    ].sort((a, b) => a.index - b.index),
  };
}

/**
 * Feedback on what the ingestion agent did: what people said, and the form for saying it.
 *
 * The agent files content into projects nobody chose by hand and then rewrites pages nobody reviewed
 * first. Its own record can say what it did and why it thought so, and nothing more — whether it was
 * *right* is a fact only a person holds, and until there is somewhere to put it the same mistake gets
 * noticed repeatedly and fixed never.
 *
 * Mounted only when a row is expanded, which is also what triggers the fetch: the list badges each
 * row with a count from its own query, and the reports themselves are worth a request only once
 * somebody is reading them.
 */
export function RunFeedback({ run, onChanged }: { run: KbRun; onChanged?: () => void }) {
  const { user } = useAuth();
  const [items, setItems] = useState<RoutingFeedback[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  const shape = shapeOf(run);

  const load = useCallback(() => {
    api.ingestFeedback
      .list(run.id)
      .then(setItems)
      .catch((err: Error) => setError(err.message));
  }, [run.id]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id: string) => {
    setError(null);
    try {
      await api.ingestFeedback.remove(id);
      load();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove that');
    }
  };

  return (
    <div className="mt-2 rounded-md border border-border-subtle bg-surface-subtle px-3 py-2.5 space-y-2.5">
      {error && <p className="text-[11px] text-danger-text">{error}</p>}

      {items === null ? (
        <p className="flex items-center gap-1.5 text-[11px] text-ink-faint">
          <Loader2 size={11} className="animate-spin" />
          Loading feedback…
        </p>
      ) : items.length === 0 ? (
        <p className="text-[11px] text-ink-faint">
          Nobody has said whether this was right. If you know, it is worth a line — nothing else in the
          system can tell.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <FeedbackEntry
              key={item.id}
              item={item}
              mine={item.createdBy.id === user?.id}
              onRemove={() => remove(item.id)}
            />
          ))}
        </ul>
      )}

      {composing ? (
        <FeedbackForm
          runId={run.id}
          shape={shape}
          onCancel={() => setComposing(false)}
          onSaved={() => {
            setComposing(false);
            load();
            onChanged?.();
          }}
        />
      ) : (
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setComposing(true)}>
            <MessageSquarePlus size={12} className="mr-1.5" />
            Give feedback
          </Button>
          {/* The one-click positive. Recording that a run was right is what gives the queue a
              denominator — without it the agent is only ever measured by its complaints. */}
          <QuickGood runId={run.id} onSaved={() => { load(); onChanged?.(); }} />
        </div>
      )}
    </div>
  );
}

function FeedbackEntry({
  item,
  mine,
  onRemove,
}: {
  item: RoutingFeedback;
  mine: boolean;
  onRemove: () => void;
}) {
  const kind = routingFeedbackKind(item.kind);
  const status = routingFeedbackStatus(item.status);

  return (
    <li className="text-[11px] space-y-0.5">
      <div className="flex items-center gap-1.5 flex-wrap">
        <Badge variant={kind.positive ? 'success' : 'warning'}>{kind.label}</Badge>

        {/* What the verdict is *about*. Absent means the arrival as a whole, which is the common
            case and needs no label. */}
        {item.project && <span className="text-ink-muted">on {item.project.name}</span>}
        {item.segmentIndex !== null && <span className="text-ink-faint">part {item.segmentIndex}</span>}
        {item.suggestedProject && (
          <span className="text-ink-muted">→ should be {item.suggestedProject.name}</span>
        )}

        {/* Only once somebody has acted. An untouched report saying "Open" is noise on this side —
            the reporter's question is whether it was read, not which queue it sits in. */}
        {item.status !== 'OPEN' && <Badge variant="muted">{status.label}</Badge>}

        <span className="ml-auto flex items-center gap-2 text-ink-faint">
          {item.createdBy.name ?? item.createdBy.email} · {timeAgo(item.createdAt)}
          {mine && (
            <button
              onClick={onRemove}
              title="Withdraw this"
              className="text-ink-faint hover:text-danger-text transition-colors"
            >
              <Trash2 size={11} />
            </button>
          )}
        </span>
      </div>

      {item.comment && <p className="text-ink-secondary leading-snug">{item.comment}</p>}

      {item.resolutionNote && (
        <p className="text-ink-faint leading-snug">
          <span className="text-ink-muted">{status.label}:</span> {item.resolutionNote}
          {item.resolvedBy && <> — {item.resolvedBy.name ?? item.resolvedBy.email}</>}
        </p>
      )}
    </li>
  );
}

/**
 * "This was right", in one press.
 *
 * A full form for a positive verdict would mean nobody ever leaves one, and a queue of only
 * complaints cannot answer "is the router getting better" — it can only grow.
 */
function QuickGood({ runId, onSaved }: { runId: string; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      await api.ingestFeedback.add(runId, { kind: 'GOOD' });
      setDone(true);
      onSaved();
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-success-text">
        <Check size={11} />
        Marked as right
      </span>
    );
  }

  return (
    <Button size="sm" variant="ghost" onClick={send} disabled={busy}>
      {busy ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <ThumbsUp size={12} className="mr-1.5" />}
      This was right
    </Button>
  );
}

function FeedbackForm({
  runId,
  shape,
  onCancel,
  onSaved,
}: {
  runId: string;
  shape: RunShape;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { allProjects } = useProjectTree();
  const [kind, setKind] = useState<RoutingFeedbackKind>('WRONG_PROJECT');
  const [projectId, setProjectId] = useState('');
  const [suggestedProjectId, setSuggestedProjectId] = useState('');
  const [segmentIndex, setSegmentIndex] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const definition = routingFeedbackKind(kind);
  const segments = shape.parts;
  // Only the projects this run actually reached can be the subject of "went to the wrong project" —
  // offering the whole tree there would invite a report about a project that never received it,
  // which is `MISSED_PROJECT` and a different claim.
  const destinations = shape.destinations;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body: RoutingFeedbackInput = {
        kind,
        ...(definition.needsProject && projectId ? { projectId } : {}),
        ...(definition.needsSuggestion && suggestedProjectId ? { suggestedProjectId } : {}),
        ...(segmentIndex ? { segmentIndex: Number(segmentIndex) } : {}),
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      };
      await api.ingestFeedback.add(runId, body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2 border-t border-border-subtle pt-2.5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block">
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-ink-faint mb-1">
            What happened
          </span>
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value as RoutingFeedbackKind)}
            className="text-xs"
          >
            {ROUTING_FEEDBACK_KINDS.map((option) => (
              <option key={option.kind} value={option.kind}>
                {option.label}
              </option>
            ))}
          </Select>
        </label>

        {/* Which piece, when the source was cut up. A split arrival is usually wrong in one part and
            right in the rest, and a report that cannot say which is hard to act on. */}
        {segments.length > 1 && (
          <label className="block">
            <span className="block text-[10px] font-semibold uppercase tracking-wide text-ink-faint mb-1">
              Which item
            </span>
            <Select value={segmentIndex} onChange={(e) => setSegmentIndex(e.target.value)} className="text-xs">
              <option value="">The whole run</option>
              {segments.map((segment) => (
                <option key={segment.index} value={segment.index}>
                  {segment.index} of {segments.length} — {segment.title?.trim() || 'untitled'}
                </option>
              ))}
            </Select>
          </label>
        )}

        {definition.needsProject && destinations.length > 0 && (
          <label className="block">
            <span className="block text-[10px] font-semibold uppercase tracking-wide text-ink-faint mb-1">
              Which project
            </span>
            <Select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="text-xs">
              <option value="">Any of them</option>
              {destinations.map((target) => (
                <option key={target.projectId} value={target.projectId}>
                  {target.label}
                </option>
              ))}
            </Select>
          </label>
        )}

        {definition.needsSuggestion && (
          <label className="block">
            <span className="block text-[10px] font-semibold uppercase tracking-wide text-ink-faint mb-1">
              Where it should have gone
            </span>
            <Select
              value={suggestedProjectId}
              onChange={(e) => setSuggestedProjectId(e.target.value)}
              className="text-xs"
            >
              <option value="">Not sure</option>
              {allProjects.map((project) => (
                <option key={project.id} value={project.id}>
                  {'  '.repeat(Math.max(project.depth - 1, 0))}
                  {project.name}
                </option>
              ))}
            </Select>
          </label>
        )}
      </div>

      <p className="text-[10px] text-ink-faint leading-snug">{definition.description}</p>

      <Textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={2}
        placeholder="What went wrong, and what you'd have expected…"
        className="text-xs"
      />

      {error && <p className="text-[11px] text-danger-text">{error}</p>}

      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" onClick={save} disabled={saving}>
          {saving ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Check size={12} className="mr-1.5" />}
          Send feedback
        </Button>
      </div>
    </div>
  );
}

/** The count badge a history row carries, so "somebody already flagged this" is visible unopened. */
export function FeedbackBadge({ total, open }: { total: number; open: number }) {
  if (total === 0) return null;
  return (
    <span
      title={open > 0 ? `${open} of ${total} still open` : `${total} reported, all triaged`}
      className={cn(
        'inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full',
        open > 0
          ? 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400'
          : 'bg-surface-muted text-ink-muted',
      )}
    >
      <MessageSquarePlus size={9} />
      {total}
    </span>
  );
}
